// GENERADO — no editar a mano. Origen: backend/src/oficina/compartido/.
// Regenerar: node scripts/gen-oficina-compartido.mjs

// Detector de tecnicismos para todo texto que ve Pau (docs/oficina-v2/DISENO-OFICINA-V2.html §7, §15.1).
// Devuelve los fragmentos que Pau no entiende; lista vacía = texto apto. Código PURO (se copia a la web).

// Límites de palabra que entienden acentos: con \b de JS, «próximo» contenía «pr».
const L = '(?<![\\p{L}\\p{N}_])';
const R = '(?![\\p{L}\\p{N}_])';
const re = (cuerpo: string, flags = 'gu') => new RegExp(L + '(?:' + cuerpo + ')' + R, flags);

const PATRONES: Array<{ que: string; re: RegExp }> = [
  { que: 'fichero', re: re('[\\w-]+\\.(?:py|ts|tsx|js|jsx|mjs|md|json|sql|sh|ya?ml|html|css|env|log)', 'giu') },
  // Ruta: al menos dos «/» con letras (así «27/09», «ida/vuelta» o «A/B» no cuentan).
  { que: 'ruta', re: re('\\.{0,2}\\/?[\\p{L}\\w.-]*\\p{L}[\\p{L}\\w.-]*\\/[\\p{L}\\w.-]+\\/[\\p{L}\\w./-]*') },
  { que: 'identificador', re: re('\\w*_\\w+') }, // ninguna palabra de Pau lleva guion bajo
  { que: 'identificador', re: re('[a-z]{2,}[A-Z][A-Za-z0-9]*') }, // «sendMessage» sí; «iPhone» no
  { que: 'código', re: /`[^`]+`|[\p{L}\w]+\(\)/gu },
  // Hash de git: 7+ hexadecimales con al menos una letra (un número largo no es un hash).
  { que: 'identificador de git', re: re('(?=[0-9a-f]*[a-f])[0-9a-f]{7,40}') },
  { que: 'git', re: re('commits?|branch(?:es)?|ramas?|merge|rebase|pull request|worktrees?|stash', 'giu') },
  { que: 'git', re: re('PR|HEAD') },
  // Pruebas: el recuento o el nombre de la herramienta, no la palabra «test» (Pau dice «blind test»).
  { que: 'pruebas', re: re('\\d+\\s+(?:tests?|pruebas)|pytest|vitest|passed|failed', 'giu') },
  { que: 'servidor', re: re('vps\\d+|runner|localhost|\\d{1,3}(?:\\.\\d{1,3}){3}', 'giu') },
];

// Palabras del negocio que sí son de Pau aunque parezcan técnicas.
const EXCEPCIONES = new Set(['ppv', 'poke', 'pokes', 'fan', 'fans', 'tip', 'tips', 'custom', 'customs', 'csm', 'clickup', 'calypso', 'venuz', 'santi', 'follow-up', 'follow-ups']);

// 27-10 (Pau: «¿por qué los agentes no me pueden enviar links?»): un enlace es para pinchar, no para leer;
// sus barras y su dominio no son jerga. Se quita antes de revisar; de un enlace con nombre [texto](enlace)
// queda el texto, que Pau sí lee. Solo con http(s):// delante: «github.com/a/b» suelto sigue siendo una ruta.
const ENLACE_CON_NOMBRE = /\[([^\]]*)\]\(https?:\/\/[^\s)]+\)/gu;
const ENLACE = /https?:\/\/[^\s<>()]+/gu;
const sinEnlaces = (texto: string) => texto.replace(ENLACE_CON_NOMBRE, '$1').replace(ENLACE, ' ');

export function detectarTecnicismos(texto: string): string[] {
  const hallados = new Set<string>();
  const legible = sinEnlaces(texto);
  for (const { re } of PATRONES) {
    for (const m of legible.matchAll(re)) {
      const frag = m[0].trim();
      if (frag && !EXCEPCIONES.has(frag.toLowerCase())) hallados.add(frag);
    }
  }
  return [...hallados];
}

export function mensajeRechazo(hallados: string[]): string {
  return `contiene ${hallados.map((h) => `"${h}"`).join(', ')}, que Pau no entiende. Dilo por lo que significa, sin nombres técnicos.`;
}

/** ¿Parece una clave (API key, token)? En un chat o un encargo se quedaría guardada para siempre
 *  (§12.4). Lo aplican la web (aviso y bloqueo al enviar) y el servidor (rechazo). */
export function pareceSecreto(texto: string): boolean {
  return /\b(pk_\d{3,}_[A-Z0-9]{8,}|sk-[A-Za-z0-9-]{16,}|ghp_[A-Za-z0-9]{20,}|ops_ey[A-Za-z0-9._-]{20,}|eyJ[A-Za-z0-9._-]{60,})\b/.test(texto);
}

// 13-10 (Pau: «me enviáis un montón de mensajes»; medido: 1 de cada 4 mensajes de los agentes, en inglés,
// aunque el reglamento lo prohíbe y Pau no lee inglés). Cuenta palabras vacías muy frecuentes de cada idioma:
// con texto corto o mezclado (nombres propios, «blind test», «PPV») decide el español. Sin dependencias.
const VACIAS_EN = new Set(['the', 'and', 'is', 'are', 'was', 'were', 'it', "it's", 'this', 'that', 'with', 'for', 'of', 'to', 'now', "i'll", "i'm", 'i', 'you', 'we', 'let', 'me', 'done', 'running', 'which', 'will', 'have', 'has', 'not', 'but', 'from', 'be', 'on', 'in', 'at', 'so', 'then', 'still', 'all', 'there']);
const VACIAS_ES = new Set(['el', 'la', 'los', 'las', 'de', 'del', 'que', 'y', 'es', 'en', 'un', 'una', 'por', 'para', 'con', 'no', 'lo', 'se', 'al', 'ya', 'te', 'tu', 'su', 'mi', 'más', 'pero', 'como', 'está', 'son', 'hay', 'ahora', 'he', 'si', 'sí', 'o']);
export function pareceIngles(texto: string): boolean {
  const palabras = texto.toLowerCase().match(/[\p{L}']+/gu) ?? [];
  let en = 0;
  let es = 0;
  for (const p of palabras) {
    if (VACIAS_EN.has(p)) en += 1;
    else if (VACIAS_ES.has(p)) es += 1;
  }
  return en >= 3 && en > es * 3;
}
